import { Fragment } from 'react'

import styled from '@emotion/styled'

import { ArcadeDivider, spacing, Text } from '@cash-design-system/react'
import {
  DisclosureBlockerBodyContent,
  DisclosureBlockerHorizontalLineItemGroup,
} from '@cash-web/protos/squareup/franklin/blockers/disclosure_blocker.pb'

import { structuredDisclosureTextTokenizer } from '../DisclaimerTextTokenizer/DisclaimerTextTokenizer'
import LineItems from '../LineItems/LineItems'

const HorizontalItems = styled.dl<{ itemCount: number }>`
  display: grid;
  grid-template-columns: repeat(${({ itemCount }) => itemCount}, minmax(0, 1fr));
  grid-template-rows: auto auto;
  grid-auto-flow: column;
  column-gap: ${spacing.xs};
  margin: 0;
`

const HorizontalTitle = styled(Text)`
  min-width: 0;
  align-self: start;
  margin: 0;
  overflow-wrap: normal;
  text-wrap: balance;
`

const HorizontalDetail = styled(Text)<{ minimumSize?: number }>`
  min-width: 0;
  margin: 0;
  /* stylelint-disable @cash-web/no-hardcoded-font-styles -- Disclosure amounts require bold weight and a server-provided minimum size. */
  font-size: ${({ minimumSize }) => (minimumSize === undefined ? undefined : `max(1rem, ${minimumSize}px)`)};
  font-weight: 500;
  /* stylelint-enable @cash-web/no-hardcoded-font-styles */
  overflow-wrap: anywhere;
  white-space: normal;
`

const HorizontalLineItems = ({ group }: { group: DisclosureBlockerHorizontalLineItemGroup }) => {
  const items = group.items ?? []

  if (items.length === 0) return null

  return (
    <HorizontalItems itemCount={items.length}>
      {items.map((item, index) => (
        <Fragment key={index}>
          <HorizontalTitle as="dt" typography="bodySmall" color="textStandard">
            {item.title_text}
          </HorizontalTitle>
          <HorizontalDetail
            as="dd"
            typography="labelMedium"
            color="textStandard"
            minimumSize={group.detail_text_min_size}
          >
            {item.detail_text}
          </HorizontalDetail>
        </Fragment>
      ))}
    </HorizontalItems>
  )
}

const StructuredBodyElement = ({ element }: { element: DisclosureBlockerBodyContent }) => {
  if (element.horizontal_line_item_group) {
    return <HorizontalLineItems group={element.horizontal_line_item_group} />
  }

  if (element.vertical_line_item_group) {
    return <LineItems compact lineItems={element.vertical_line_item_group.items ?? []} typography="bodyMedium" />
  }

  if (element.text_line_item) {
    return (
      <Text as="div" color="textSubtle">
        {structuredDisclosureTextTokenizer([element.text_line_item.text])}
      </Text>
    )
  }

  if (element.divider_element) {
    return <ArcadeDivider />
  }

  return null
}

export const StructuredDisclosureBody = ({ content }: { content: DisclosureBlockerBodyContent[] }) => (
  <div>
    {content.map((element, index) => (
      <StructuredBodyElement element={element} key={index} />
    ))}
  </div>
)

export default StructuredDisclosureBody
